import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import BreadcrumbTrail from '../../components/navigation/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CSVUploadArea from './components/CSVUploadArea';
import PlayListingCard from './components/PlayListingCard';
import ProcessingStatus from './components/ProcessingStatus';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import EmptyState from './components/EmptyState';
import UploadErrorReport from './components/UploadErrorReport';
import CSVUploadSuccessPanel from './components/CSVUploadSuccessPanel';
import { getAllPlays, deletePlay, createPlayWithContent, searchPlaysAdvanced } from '../../services/playsService';
import { parseShakespeareCSV, validateRowsDetailed } from '../../utils/csvProcessor';
import { scanAndPopulateGlossary } from '../../services/glossaryAutoPopulateService';

const PlayManagement = () => {
  const { user, userProfile, isAuthenticated } = useAuth();
  const [plays, setPlays] = useState([]);
  const [filteredPlays, setFilteredPlays] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [progressStats, setProgressStats] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, play: null });
  const [selectedPlays, setSelectedPlays] = useState([]);
  const [sortBy, setSortBy] = useState('mostAudio');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('box'); // 'box' or 'line'
  const [isSearching, setIsSearching] = useState(false);
  const [uploadRowErrors, setUploadRowErrors] = useState([]);
  const [uploadSuccessData, setUploadSuccessData] = useState(null); // { csvAnalysis, fileName }
  const [glossaryStatus, setGlossaryStatus] = useState(null); // { status: 'scanning'|'done'|'error', message, added, skipped }

  // Fetch plays from Supabase
  useEffect(() => {
    const fetchPlays = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await getAllPlays();
        setPlays(data);
        setFilteredPlays(data);
      } catch (err) {
        console.error('Error fetching plays:', err);
        setError('Failed to load plays. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchPlays();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery || searchQuery?.trim() === '') {
        // No search query, show all plays with sorting
        let filtered = [...plays];

        switch (sortBy) {
          case 'alpha':
            filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
            break;
          case 'lastUpdated':
            filtered?.sort((a, b) => new Date(b?.lastModified) - new Date(a?.lastModified));
            break;
          case 'mostAudio':
            filtered?.sort((a, b) => (b?.audioFilesUploaded || 0) - (a?.audioFilesUploaded || 0));
            break;
          case 'mostGlossary':
            filtered?.sort((a, b) => (b?.glossaryTermsAssigned || 0) - (a?.glossaryTermsAssigned || 0));
            break;
          case 'mostPopular':
            filtered?.sort((a, b) => (b?.studentCount || 0) - (a?.studentCount || 0));
            break;
          case 'lastAdded':
            filtered?.sort((a, b) => new Date(b?.uploadDate) - new Date(a?.uploadDate));
            break;
          default:
            break;
        }

        setFilteredPlays(filtered);
        return;
      }

      // Perform advanced search
      try {
        setIsSearching(true);
        const matchingPlayIds = await searchPlaysAdvanced(searchQuery);
        
        // Filter plays by matching IDs
        let filtered = plays?.filter(play => matchingPlayIds?.includes(play?.id));

        // Apply sorting
        switch (sortBy) {
          case 'alpha':
            filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
            break;
          case 'lastUpdated':
            filtered?.sort((a, b) => new Date(b?.lastModified) - new Date(a?.lastModified));
            break;
          case 'mostAudio':
            filtered?.sort((a, b) => (b?.audioFilesUploaded || 0) - (a?.audioFilesUploaded || 0));
            break;
          case 'mostGlossary':
            filtered?.sort((a, b) => (b?.glossaryTermsAssigned || 0) - (a?.glossaryTermsAssigned || 0));
            break;
          case 'mostPopular':
            filtered?.sort((a, b) => (b?.studentCount || 0) - (a?.studentCount || 0));
            break;
          case 'lastAdded':
            filtered?.sort((a, b) => new Date(b?.uploadDate) - new Date(a?.uploadDate));
            break;
          default:
            break;
        }

        setFilteredPlays(filtered);
      } catch (err) {
        console.error('Search error:', err);
        setError('Search failed. Please try again.');
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce search to avoid too many requests
    const timeoutId = setTimeout(() => {
      performSearch();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, plays, sortBy]);

  const handleFileUpload = async (file) => {
    setIsProcessing(true);
    setProcessingStatus('processing');
    setProcessingProgress(0);
    setProgressStats(null);
    setUploadRowErrors([]);
    setUploadSuccessData(null);

    // Estimate total rows from file size (avg ~80 bytes/row)
    const estimatedTotalRows = Math.max(1, Math.round(file?.size / 80));
    const startTime = Date.now();
    let lastUpdateTime = startTime;
    let lastProcessedRows = 0;

    try {
      // Simulate row-level progress during CSV parsing phase (0–70%)
      let simulatedRows = 0;
      const progressInterval = setInterval(() => {
        const elapsed = (Date.now() - startTime) / 1000;
        // Increment simulated rows at a realistic pace
        const increment = Math.max(1, Math.round(estimatedTotalRows * 0.05));
        simulatedRows = Math.min(simulatedRows + increment, Math.round(estimatedTotalRows * 0.7));

        const progressPct = Math.min(70, Math.round((simulatedRows / estimatedTotalRows) * 70));
        setProcessingProgress(progressPct);

        // Calculate speed (rows/sec)
        const timeDelta = (Date.now() - lastUpdateTime) / 1000;
        const rowDelta = simulatedRows - lastProcessedRows;
        const speed = timeDelta > 0 ? Math.round(rowDelta / timeDelta) : 0;
        lastUpdateTime = Date.now();
        lastProcessedRows = simulatedRows;

        // Calculate ETA
        const remainingRows = estimatedTotalRows - simulatedRows;
        const eta = speed > 0 ? remainingRows / speed : null;

        setProgressStats({
          processedRows: simulatedRows,
          totalRows: estimatedTotalRows,
          speed: speed,
          eta: eta
        });
      }, 400);

      // Parse CSV with comprehensive analysis
      const csvAnalysis = await parseShakespeareCSV(file);
      clearInterval(progressInterval);

      // Run per-row validation and surface errors
      const rowErrors = validateRowsDetailed(csvAnalysis?.rawData ?? []);
      setUploadRowErrors(rowErrors);

      // After parsing: we know the real row count
      const realTotalRows = csvAnalysis?.totalLines ?? estimatedTotalRows;
      const parseElapsed = (Date.now() - startTime) / 1000;
      const parseSpeed = parseElapsed > 0 ? Math.round(realTotalRows / parseElapsed) : 0;

      setProcessingProgress(80);
      setProgressStats({
        processedRows: realTotalRows,
        totalRows: realTotalRows,
        speed: parseSpeed,
        eta: null
      });

      // Create play with all content (play, scenes, lines)
      const playData = {
        title: file?.name?.replace('.csv', '')?.replace(/_/g, ' '),
        author: 'William Shakespeare',
        description: `A Shakespeare play with ${csvAnalysis?.totalActs} Acts, ${csvAnalysis?.totalScenes} Scenes, ${csvAnalysis?.totalLines} Lines, and ${csvAnalysis?.characterCount} unique Characters`,
        is_published: false,
        year_written: 1597
      };

      // DB insert phase: 80–100%
      const dbStart = Date.now();
      let dbRows = 0;
      const dbInterval = setInterval(() => {
        const dbElapsed = (Date.now() - dbStart) / 1000;
        dbRows = Math.min(dbRows + Math.round(realTotalRows * 0.1), realTotalRows);
        const dbProgress = 80 + Math.round((dbRows / realTotalRows) * 18);
        const dbSpeed = dbElapsed > 0 ? Math.round(dbRows / dbElapsed) : 0;
        const dbRemaining = realTotalRows - dbRows;
        const dbEta = dbSpeed > 0 ? dbRemaining / dbSpeed : null;
        setProcessingProgress(Math.min(98, dbProgress));
        setProgressStats({
          processedRows: dbRows,
          totalRows: realTotalRows,
          speed: dbSpeed,
          eta: dbEta
        });
      }, 400);

      const newPlay = await createPlayWithContent(playData, csvAnalysis, user?.id);
      clearInterval(dbInterval);

      // Add to plays list with enriched data
      const enrichedPlay = {
        id: newPlay?.id,
        title: newPlay?.title,
        author: newPlay?.author,
        description: newPlay?.description,
        actCount: csvAnalysis?.totalActs,
        sceneCount: csvAnalysis?.totalScenes,
        lineCount: csvAnalysis?.totalLines,
        characterCount: csvAnalysis?.characterCount,
        studentCount: 0,
        audioFilesUploaded: 0,
        audioCompletion: 0,
        glossaryTermsAssigned: 0,
        glossaryCompletion: 0,
        uploadDate: newPlay?.created_at,
        lastModified: newPlay?.updated_at,
        isPublished: newPlay?.is_published,
        coverImageUrl: newPlay?.cover_image_url,
        difficulty: newPlay?.difficulty,
        yearWritten: newPlay?.year_written
      };

      setPlays(prev => [enrichedPlay, ...prev]);
      setProcessingProgress(100);
      setProgressStats({
        processedRows: realTotalRows,
        totalRows: realTotalRows,
        speed: parseSpeed,
        eta: 0
      });
      setProcessingStatus('success');
      setIsProcessing(false);

      // Store analysis for success panel
      setUploadSuccessData({ csvAnalysis, fileName: file?.name });

      // Trigger async glossary auto-population (non-blocking)
      setGlossaryStatus({ status: 'scanning', message: 'Scanning lines for archaic words...' });
      scanAndPopulateGlossary(
        csvAnalysis?.lines || [],
        newPlay?.id,
        newPlay?.title,
        user?.id,
        (msg) => setGlossaryStatus({ status: 'scanning', message: msg })
      )?.then(result => {
          setGlossaryStatus({
            status: 'done',
            message: `Glossary scan complete: ${result?.added} new term${result?.added !== 1 ? 's' : ''} added, ${result?.skipped} already existed.`,
            added: result?.added,
            skipped: result?.skipped
          });
        })?.catch(err => {
          setGlossaryStatus({ status: 'error', message: 'Glossary scan encountered an error. You can run it manually from Glossary Management.' });
        });

      console.log('✅ Play created successfully:', {
        title: newPlay?.title,
        acts: csvAnalysis?.totalActs,
        scenes: csvAnalysis?.totalScenes,
        lines: csvAnalysis?.totalLines,
        characters: csvAnalysis?.characterCount
      });

      setTimeout(() => {
        setProcessingStatus(null);
        setProcessingProgress(0);
        setProgressStats(null);
      }, 3000);
    } catch (error) {
      console.error('CSV processing error:', error);
      setProcessingStatus('error');
      setError(error?.message || 'Failed to process CSV file. Please check the file format.');
      setIsProcessing(false);
      setProgressStats(null);

      setTimeout(() => {
        setProcessingStatus(null);
        setProcessingProgress(0);
      }, 5000);
    }
  };

  const handleEditPlay = async (updatedPlay) => {
    // Update the play in the local state
    setPlays(prev => prev?.map(p => 
      p?.id === updatedPlay?.id ? { ...p, ...updatedPlay } : p
    ));
  };

  const handleDeletePlay = (play) => {
    setDeleteModal({ isOpen: true, play });
  };

  const confirmDelete = async (playId) => {
    try {
      await deletePlay(playId);
      setPlays(prev => prev?.filter(p => p?.id !== playId));
      setDeleteModal({ isOpen: false, play: null });
    } catch (error) {
      console.error('Error deleting play:', error);
      setError('Failed to delete play. Please try again.');
    }
  };

  const handleBulkAction = (action, playIds) => {
    console.log(`Executing ${action} on plays:`, playIds);
    setSelectedPlays([]);
  };

  const scrollToUpload = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={userProfile}
        isAuthenticated={isAuthenticated}
        notifications={[]}
      />
      <main className="main-content">
        <BreadcrumbTrail />

        <div className="max-w-screen-2xl mx-auto px-6 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Theater" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Play Management</h1>
                <p className="text-muted-foreground">Upload and manage Shakespeare play content</p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <CSVUploadArea 
              onFileUpload={handleFileUpload} 
              isProcessing={isProcessing}
              existingPlays={plays}
            />

            {processingStatus && (
              <ProcessingStatus
                status={processingStatus}
                progress={processingProgress}
                message={
                  processingStatus === 'processing' ? 'Processing CSV file and generating line IDs...'
                    : processingStatus === 'success'? 'Play successfully uploaded and processed!' :'Error processing file'
                }
                progressStats={progressStats}
              />
            )}

            {uploadSuccessData && (
              <CSVUploadSuccessPanel
                csvAnalysis={uploadSuccessData?.csvAnalysis}
                fileName={uploadSuccessData?.fileName}
                glossaryStatus={glossaryStatus}
                onDismiss={() => { setUploadSuccessData(null); setGlossaryStatus(null); }}
              />
            )}

            {uploadRowErrors?.length > 0 && (
              <UploadErrorReport errors={uploadRowErrors} />
            )}

            {error && (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : plays?.length > 0 ? (
              <>
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 relative">
                    <Input
                      type="search"
                      placeholder="Search plays by title, line (partial phrase fuzzy search), character or setting..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e?.target?.value)}
                      className="w-full"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e?.target?.value)}
                      className="px-4 py-2 rounded-md border border-border bg-card text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="lastAdded">Last Added</option>
                      <option value="alpha">Alphabetical (A-Z)</option>
                      <option value="lastUpdated">Last Updated</option>
                      <option value="mostAudio">Most Audio Files</option>
                      <option value="mostGlossary">Most Glossary Words</option>
                      <option value="mostPopular">Most Popular</option>
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Filter"
                    >
                      Filter
                    </Button>
                    <div className="flex items-center gap-1 border border-border rounded-md p-1">
                      <button
                        onClick={() => setViewMode('box')}
                        className={`p-2 rounded transition-colors duration-150 ${
                          viewMode === 'box' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-muted-foreground'
                        }`}
                        aria-label="Box view"
                      >
                        <Icon name="LayoutGrid" size={18} />
                      </button>
                      <button
                        onClick={() => setViewMode('line')}
                        className={`p-2 rounded transition-colors duration-150 ${
                          viewMode === 'line' ?'bg-primary text-primary-foreground' :'hover:bg-muted text-muted-foreground'
                        }`}
                        aria-label="Line view"
                      >
                        <Icon name="List" size={18} />
                      </button>
                    </div>
                  </div>
                </div>

                <BulkOperationsPanel
                  selectedPlays={selectedPlays}
                  onBulkAction={handleBulkAction}
                  onClearSelection={() => setSelectedPlays([])}
                />

                <div className={viewMode === 'box' ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-3'}>
                  {filteredPlays?.map(play => (
                    <PlayListingCard
                      key={play?.id}
                      play={play}
                      onEdit={handleEditPlay}
                      onDelete={handleDeletePlay}
                      viewMode={viewMode}
                    />
                  ))}
                </div>

                {filteredPlays?.length === 0 && (
                  <div className="bg-card rounded-lg border border-border p-12 text-center">
                    <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-card-foreground mb-2">
                      No plays found
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Try adjusting your search query or filters
                    </p>
                  </div>
                )}
              </>
            ) : (
              <EmptyState onUploadClick={scrollToUpload} />
            )}
          </div>
        </div>
      </main>
      <DeleteConfirmationModal
        isOpen={deleteModal?.isOpen}
        play={deleteModal?.play}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteModal({ isOpen: false, play: null })}
      />
    </div>
  );
};

export default PlayManagement;